import { inject, Injectable } from '@angular/core';
import { Actions, ofActionDispatched, ofActionSuccessful, Store } from '@ngxs/store';
import { map, merge, Observable } from 'rxjs';

import { ImageContent, ImageEditorTagsGateway } from '@efficiently/client-api-image-editor';
import { TagResult, TagsRepository, TagToContentInput } from '@efficiently/client-api-tags';

import { UnassignTagFromContentAction } from '@ds/store/shared/images-manager';
import { CreateOrAssignTagAction, CreateTagsAction } from '@ds/store/shared/tag/tag.actions';
import { TagSelectors } from '@ds/store/shared/tag/tag.selector';

/**
 * Application-side implementation of {@link ImageEditorTagsGateway}.
 *
 * Bridges the reusable image editor widget to the design-schedule NgXS store
 * and tag GraphQL service. Lives in the app because the tag/images-manager
 * stores are app-scoped and bidirectionally coupled.
 */
@Injectable()
export class ImageEditorTagsGatewayService extends ImageEditorTagsGateway {
	private readonly store = inject(Store);
	private readonly actions$ = inject(Actions);
	private readonly tagsRepository = inject(TagsRepository);

	public readonly tagCreationSettled$: Observable<boolean> = merge(
		this.actions$.pipe(
			ofActionDispatched(CreateTagsAction),
			map(() => false),
		),
		this.actions$.pipe(
			ofActionSuccessful(CreateTagsAction),
			map(() => true),
		),
	);

	public getImageTags(contentId: string): Observable<TagResult[]> {
		return this.store.select(TagSelectors.imagesTags(contentId));
	}

	public searchTags(query: string): Observable<TagResult[]> {
		return this.tagsRepository.findTags(query);
	}

	public createOrAssignTag(name: string, content: ImageContent): void {
		this.store.dispatch(new CreateOrAssignTagAction(name, content));
	}

	public unassignTagsFromContent(assignments: TagToContentInput[]): Observable<void> {
		return this.store.dispatch(new UnassignTagFromContentAction(assignments));
	}
}
